import React from 'react';
import './SafeDrivingPage.css';

interface SafeDrivingPageProps {
  onNavigateToChatbot: () => void;
}

const SafeDrivingPage: React.FC<SafeDrivingPageProps> = ({ onNavigateToChatbot }) => {
  const handleExternalLink = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <main className="safe-driving-page">
      {/* Hero Section */}
      <section className="safe-driving-hero">
        <div className="hero-content">
          <h1>Safe Driving Tips & Techniques</h1>
          <p>Master the art of safe driving to protect yourself, your passengers, and potentially lower your insurance premiums.</p>
        </div>
      </section>

      {/* Essential Tips */}
      <section className="essential-tips">
        <div className="container">
          <h2>Essential Safe Driving Practices</h2>
          <div className="tips-grid">
            <div className="tip-card">
              <div className="tip-icon">👀</div>
              <h3>Stay Alert and Focused</h3>
              <ul>
                <li>Avoid distractions like phones, food, or loud music</li>
                <li>Take breaks every 2 hours on long trips</li>
                <li>Never drive when drowsy or under the influence</li>
                <li>Keep your eyes moving and scan the road ahead</li>
              </ul>
            </div>

            <div className="tip-card">
              <div className="tip-icon">🚗</div>
              <h3>Maintain Your Vehicle</h3>
              <ul>
                <li>Check tire pressure and tread depth regularly</li>
                <li>Keep up with scheduled maintenance</li>
                <li>Ensure all lights and signals work properly</li>
                <li>Keep windshield clean and wipers in good condition</li>
              </ul>
            </div>

            <div className="tip-card">
              <div className="tip-icon">📏</div>
              <h3>Follow the 3-Second Rule</h3>
              <ul>
                <li>Maintain safe following distance</li>
                <li>Increase distance in poor weather conditions</li>
                <li>Allow extra space around large vehicles</li>
                <li>Never tailgate - it's dangerous and aggressive</li>
              </ul>
            </div>

            <div className="tip-card">
              <div className="tip-icon">🛡️</div>
              <h3>Use Safety Equipment</h3>
              <ul>
                <li>Always wear your seatbelt</li>
                <li>Ensure all passengers are properly secured</li>
                <li>Use appropriate child safety seats</li>
                <li>Adjust mirrors and seat position before driving</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Defensive Driving */}
      <section className="defensive-driving">
        <div className="container">
          <div className="defensive-content">
            <div className="defensive-text">
              <h2>Defensive Driving Techniques</h2>
              <p>Defensive driving means being prepared for the unexpected and anticipating potential hazards before they become dangerous situations.</p>
              
              <div className="techniques-list">
                <div className="technique">
                  <h4>🔍 Scan and Anticipate</h4>
                  <p>Constantly scan the road ahead, behind, and to the sides. Look for potential hazards like pedestrians, cyclists, or erratic drivers.</p>
                </div>
                
                <div className="technique">
                  <h4>⚠️ Expect the Unexpected</h4>
                  <p>Always assume other drivers might make mistakes. Be prepared to react to sudden lane changes, stops, or turns.</p>
                </div>
                
                <div className="technique">
                  <h4>🚦 Obey Traffic Laws</h4>
                  <p>Follow speed limits, traffic signals, and road signs. They're designed to keep everyone safe on the road.</p>
                </div>
                
                <div className="technique">
                  <h4>🌙 Adjust for Conditions</h4>
                  <p>Reduce speed and increase following distance in rain, snow, fog, or heavy traffic conditions.</p>
                </div>
              </div>
            </div>
            <div className="defensive-image">
              <div className="placeholder-image">🛡️🚗</div>
            </div>
          </div>
        </div>
      </section>

      {/* Weather Conditions */}
      <section className="weather-driving">
        <div className="container">
          <h2>Driving in Different Weather Conditions</h2>
          <div className="weather-grid">
            <div className="weather-card">
              <div className="weather-icon">🌧️</div>
              <h3>Rainy Weather</h3>
              <ul>
                <li>Reduce speed by 5-10 mph</li>
                <li>Turn on headlights</li>
                <li>Avoid sudden movements</li>
                <li>Use windshield wipers</li>
                <li>Watch for hydroplaning</li>
              </ul>
            </div>

            <div className="weather-card">
              <div className="weather-icon">❄️</div>
              <h3>Snow and Ice</h3>
              <ul>
                <li>Drive slowly and smoothly</li>
                <li>Brake gently and early</li>
                <li>Increase following distance to 8-10 seconds</li>
                <li>Use snow tires or chains when required</li>
                <li>Clear all snow from vehicle before driving</li>
              </ul>
            </div>

            <div className="weather-card">
              <div className="weather-icon">🌫️</div>
              <h3>Fog</h3>
              <ul>
                <li>Use low-beam headlights</li>
                <li>Never use high beams in fog</li>
                <li>Reduce speed significantly</li>
                <li>Use fog lights if available</li>
                <li>Consider pulling over safely if visibility is too poor</li>
              </ul>
            </div>

            <div className="weather-card">
              <div className="weather-icon">☀️</div>
              <h3>Bright Sun</h3>
              <ul>
                <li>Use sunglasses and sun visors</li>
                <li>Keep windshield clean</li>
                <li>Be extra cautious during sunrise/sunset</li>
                <li>Watch for glare off wet roads</li>
                <li>Ensure you can see clearly before proceeding</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Videos */}
      <section className="safety-videos">
        <div className="container">
          <h2>Educational Safety Videos</h2>
          <div className="videos-grid">
            <div className="video-card">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/2DROs4iyDUg"
                  title="Defensive Driving Techniques"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-info">
                <h3>Defensive Driving Techniques</h3>
                <p>Learn essential defensive driving skills to stay safe on the road.</p>
              </div>
            </div>

            <div className="video-card">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/GaKgKLHa8OE"
                  title="Modern Car Safety Features"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-info">
                <h3>Modern Car Safety Features</h3>
                <p>Understanding how modern safety technology can help prevent accidents.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Benefits */}
      <section className="insurance-benefits">
        <div className="container">
          <div className="benefits-content">
            <h2>How Safe Driving Reduces Insurance Costs</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <div className="benefit-icon">💰</div>
                <h3>Safe Driver Discounts</h3>
                <p>Many insurers offer discounts of 10-25% for drivers with clean records and no claims.</p>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">📊</div>
                <h3>Usage-Based Insurance</h3>
                <p>Telematics programs track your driving habits and can reduce premiums for safe drivers.</p>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">🏆</div>
                <h3>Good Driver Awards</h3>
                <p>Accident-free periods and defensive driving course completions can earn additional savings.</p>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">📈</div>
                <h3>Long-term Savings</h3>
                <p>Safe driving habits compound over time, leading to significant premium reductions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="resources-section">
        <div className="container">
          <h2>Additional Safety Resources</h2>
          <div className="resources-grid">
            <div className="resource-card">
              <div className="resource-icon">🏛️</div>
              <h3>Government Resources</h3>
              <div className="resource-links">
                <button
                  onClick={() => handleExternalLink('https://www.nhtsa.gov/')}
                  className="resource-link"
                >
                  NHTSA - National Highway Traffic Safety Administration
                </button>
                <button
                  onClick={() => handleExternalLink('https://www.fmcsa.dot.gov/')}
                  className="resource-link"
                >
                  FMCSA - Federal Motor Carrier Safety Administration
                </button>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon">🎓</div>
              <h3>Driving Courses</h3>
              <div className="resource-links">
                <button
                  onClick={() => handleExternalLink('https://www.nsc.org/safety-training/defensive-driving')}
                  className="resource-link"
                >
                  National Safety Council - Defensive Driving
                </button>
                <button
                  onClick={() => handleExternalLink('https://www.aaa.com/automotive/driving-courses/')}
                  className="resource-link"
                >
                  AAA Driving Improvement Programs
                </button>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon">📚</div>
              <h3>Safety Organizations</h3>
              <div className="resource-links">
                <button
                  onClick={() => handleExternalLink('https://www.iihs.org/')}
                  className="resource-link"
                >
                  IIHS - Insurance Institute for Highway Safety
                </button>
                <button
                  onClick={() => handleExternalLink('https://www.nsc.org/')}
                  className="resource-link"
                >
                  National Safety Council
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Take Action */}
      <section className="take-action">
        <div className="container">
          <div className="action-content">
            <h2>Put Safety First</h2>
            <p>Start implementing these safe driving practices today. Your safety and your wallet will thank you.</p>
            <div className="action-buttons">
              <button
                className="btn-primary"
                onClick={onNavigateToChatbot}
              >
                Ask Our AI About Safe Driving
              </button>
              <button className="btn-secondary">
                Get a Safe Driver Quote
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SafeDrivingPage;